import { VROC } from './VROC.js';
import { XMLState } from './XMLState.js';
import { popupManager } from './PopupManager.js';

export class VROCComponentFactory {
	constructor() {
		this.vroc = new VROC();
		this.components = new Map();
		this.defaultModuleId = 47; // CRM module
		this.globalEvents = new Map(); // Глобальные события
	}

	/**
	 * Создает объект событий из функций
	 * @param {Object} eventHandlers - Объект с обработчиками событий
	 * @returns {Object} - Объект событий в правильном формате
	 */
	static createEvents(eventHandlers) {
		const events = {};
		Object.entries(eventHandlers).forEach(([eventName, handler]) => {
			events[eventName] = Array.isArray(handler) ? handler : [handler];
		});
		return events;
	}

	/**
	 * Создает и инициализирует компонент
	 * @param {Object} config - Конфигурация компонента
	 * @param {string} config.name - Название компонента (используется для генерации ID)
	 * @param {string} config.template - ID шаблона XSLT
	 * @param {string} config.node - CSS селектор для контейнера
	 * @param {Object} config.params - Параметры для AJAX запросов
	 * @param {number} config.moduleId - ID модуля (по умолчанию 47)
	 * @param {Object} config.actions - Ручные действия компонента
	 * @param {Object} config.autoActions - Автоматически генерируемые действия
	 * @param {Function} config.onInit - Callback после инициализации
	 * @param {Function} config.onError - Callback для обработки ошибок
	 * @param {Object} config.validation - Правила валидации
	 * @param {Object} config.events - События жизненного цикла
	 * @param {Object} config.notifications - Настройки уведомлений
	 * @param {boolean} config.attach - Режим привязки к существующему DOM (по умолчанию false)
	 * @returns {VROCComponent} - Экземпляр компонента
	 */
	createComponent(config) {
		// Валидация конфигурации
		this._validateConfig(config);
		
		const component = new VROCComponent(this.vroc, config, this);
		this.components.set(config.name, component);
		
		// Вызываем глобальное событие создания компонента
		this._triggerGlobalEvent('componentCreated', { component, config });
		
		return component;
	}

	/**
	 * Получает существующий компонент по имени
	 * @param {string} name - Название компонента
	 * @returns {VROCComponent} - Экземпляр компонента
	 */
	getComponent(name) {
		return this.components.get(name);
	}

	/**
	 * Уничтожает компонент
	 * @param {string} name - Название компонента
	 */
	destroyComponent(name) {
		const component = this.components.get(name);
		if (component) {
			component.destroy();
			this.components.delete(name);
			
			// Вызываем глобальное событие уничтожения компонента
			this._triggerGlobalEvent('componentDestroyed', { name, component });
		}
	}

	/**
	 * Получает все активные компоненты
	 * @returns {Map} - Map всех компонентов
	 */
	getAllComponents() {
		return new Map(this.components);
	}

	/**
	 * Уничтожает все компоненты
	 */
	destroyAllComponents() {
		const componentNames = Array.from(this.components.keys());
		componentNames.forEach(name => this.destroyComponent(name));
	}

	/**
	 * Подписывается на глобальные события
	 * @param {string} eventName - Название события
	 * @param {Function} handler - Обработчик события
	 */
	on(eventName, handler) {
		if (!this.globalEvents.has(eventName)) {
			this.globalEvents.set(eventName, []);
		}
		this.globalEvents.get(eventName).push(handler);
	}

	/**
	 * Отписывается от глобальных событий
	 * @param {string} eventName - Название события
	 * @param {Function} handler - Обработчик события
	 */
	off(eventName, handler) {
		if (this.globalEvents.has(eventName)) {
			const handlers = this.globalEvents.get(eventName);
			const index = handlers.indexOf(handler);
			if (index > -1) {
				handlers.splice(index, 1);
			}
		}
	}

	/**
	 * Вызывает глобальное событие
	 * @param {string} eventName - Название события
	 * @param {Object} data - Данные события
	 */
	_triggerGlobalEvent(eventName, data) {
		if (this.globalEvents.has(eventName)) {
			this.globalEvents.get(eventName).forEach(handler => {
				try {
					handler(data);
				} catch (error) {
					console.error(`Error in global event handler for ${eventName}:`, error);
				}
			});
		}
	}

	/**
	 * Валидирует конфигурацию компонента
	 * @param {Object} config - Конфигурация компонента
	 */
	_validateConfig(config) {
		const requiredFields = ['name', 'template', 'node'];
		const missingFields = requiredFields.filter(field => !config[field]);
		
		if (missingFields.length > 0) {
			throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
		}

		if (this.components.has(config.name)) {
			console.warn(`Component with name "${config.name}" already exists. It will be replaced.`);
		}
	}
}

class VROCComponent {
	constructor(vroc, config, factory) {
		this.vroc = vroc;
		this.config = config;
		this.factory = factory;
		this.id = `component-${config.name}`;
		this.params = config.params || {};
		this.moduleId = config.moduleId || 47;
		this.actions = config.actions || {};
		this.autoActions = config.autoActions || {};
		this.onInit = config.onInit;
		this.onError = config.onError;
		this.validation = config.validation || {};
		this.customValidation = config.customValidation || {};
		this.events = config.events || {};
		this.notifications = config.notifications || {};
		this.isDestroyed = false;
		this.isReady = false; // Флаг готовности компонента
		this.initialActionPerformed = false; // Флаг, что начальное действие уже выполнено
		this.isPopup = false;
		this.popupName = null;
		this.attach = config.attach || false;

		this.init();
	}

	async init() {
		try {
			// Устанавливаем флаг, что компонент еще не готов
			this.isReady = false;

			// Вызываем событие beforeInit
			this._triggerEvent('beforeInit');

			// Оптимизация: если есть конкретное действие, сразу делаем POST вместо GET
			if (this.params.action && this.params.action !== this.config.originalName) {
				// Есть конкретное действие - делаем POST запрос сразу
				console.log(`[VROC] Optimized: Making direct POST request for action: ${this.params.action}`);

				const formData = new FormData();
				const { action: actionParam, ...otherParams } = this.params;

				// Добавляем все параметры кроме action
				Object.entries(otherParams).forEach(([key, value]) => {
					// Если значение - массив, добавляем каждый элемент отдельно
					if (Array.isArray(value)) {
						value.forEach(item => {
							formData.append(`${key}[]`, item);
						});
					} else {
						formData.append(key, value);
					}
				});

				// Добавляем action из params
				formData.append('action', actionParam);

				const response = await fetch(`/ajax.php?mid=${this.moduleId}`, {
					method: 'POST',
					body: formData
				});

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const xmlText = await response.text();

				// Вместо data URL, создаем компонент напрямую с XML данными
				const xmlDoc = new DOMParser().parseFromString(xmlText, "text/xml");

				// Создаем компонент вручную без дополнительного fetch
				const component = {
					id: this.id,
					container: document.querySelector(this.config.node),
					domMap: new Map(),
					state: new XMLState(xmlText),
					templateId: this.config.template,
					attach: this.attach,
					config: this.config // Передаем конфигурацию для доступа в PatchProcessor
				};

				this.vroc.components.set(this.id, component);

				// Первоначальный рендеринг только если не режим attach
				if (!this.attach) {
					await this.vroc.renderFragment(this.id, component.container, xmlDoc, null, true);
				}

				// Построение карты DOM для компонента
				this.vroc._buildDomMap(this.id);

				// Отмечаем, что начальное действие уже выполнено
				this.initialActionPerformed = true;
			} else {
				// Стандартная загрузка - GET запрос
				// Создаем параметры без action, чтобы избежать путаницы
				const { action, ...otherParams } = this.params;
				const urlParams = new URLSearchParams({
					mid: this.moduleId,
					action: this.config.originalName || this.config.name,
					...otherParams
				});

				// Загружаем данные с сервера
				await this.vroc.loadComponent({
					id: this.id,
					xmlUrl: `/ajax.php?${urlParams.toString()}`,
					templateId: this.config.template,
					node: this.config.node,
					attach: this.attach, // Передаем флаг attach в VROC
					config: this.config // Передаем конфигурацию для доступа в PatchProcessor
				});
			}

			// Проверяем, является ли компонент попапом
			this._checkAndSetupPopup();

			// Генерируем автоматические действия
			this._generateAutoActions();

			// Вызываем callback инициализации
			if (this.onInit) {
				await this.onInit.call(this);
			}

			// Вызываем событие afterInit
			this._triggerEvent('afterInit');

			// Небольшая задержка для гарантии завершения всех операций
			await new Promise(resolve => setTimeout(resolve, 10));

			// Устанавливаем флаг готовности компонента
			this.isReady = true;

		} catch (error) {
			this._handleError('Initialization failed', error);
		}
	}

	/**
	 * Проверяет, является ли компонент попапом и настраивает его
	 */
	_checkAndSetupPopup() {
		// Инициализируем глобальные события попапов (если еще не инициализированы)
		popupManager.initGlobal();

		// Находим контейнер компонента
		const container = document.querySelector(this.config.node);
		if (!container) return;

		// Проверяем, является ли контейнер попапом
		if (popupManager.isPopup(container)) {
			this.isPopup = true;
			this.popupName = container.getAttribute('data-popup-name');
			
			// Привязываем события попапа только если это не режим attach
			// В режиме attach события уже должны быть привязаны
			if (!this.attach) {
				popupManager.bindPopupEvents(container);
			}
			
			// Подписываемся на событие закрытия от PopupManager
			this._subscribeToPopupEvents();
			
			console.log(`[${this.config.name}] Component detected as popup: ${this.popupName} (attach: ${this.attach})`);
		}
	}

	/**
	 * Подписывается на события закрытия попапа от PopupManager
	 */
	_subscribeToPopupEvents() {
		// Создаем уникальный обработчик для этого компонента
		this._popupCloseHandler = (popupName) => {
			// Проверяем, что это наш попап, компонент не уничтожен, и попап действительно закрыт
			if (popupName === this.popupName && !this.isDestroyed && !this._isBeingDestroyed) {
				const popup = document.querySelector(this.config.node);
				// Дополнительная проверка: попап должен действительно быть неактивным
				if (popup && !popup.classList.contains('active')) {
					console.log(`[${this.config.name}] Popup ${popupName} closed, triggering hidden event`);
					this._triggerEvent('hidden');
				}
			}
		};

		// Подписываемся на глобальное событие закрытия попапа
		if (window.popupManager) {
			window.popupManager.on('popupClosed', this._popupCloseHandler);
		}
	}

	/**
	 * Генерирует автоматические действия на основе autoActions
	 */
	_generateAutoActions() {
		Object.entries(this.autoActions).forEach(([actionName, config]) => {
			const methodName = config.methodName || actionName;
			
			// Создаем метод действия и добавляем его к компоненту
			this[methodName] = async (...args) => {
				try {
					// Подготавливаем данные для отправки
					let data = {};
					
					if (config.dataMapper) {
						// Используем кастомный маппер данных
						data = config.dataMapper(...args, this.params);
					} else if (config.paramNames) {
						// Используем имена параметров
						config.paramNames.forEach((paramName, index) => {
							if (args[index] !== undefined) {
								data[paramName] = args[index];
							}
						});
					} else {
						// Простое сопоставление по позиции
						data = args[0] || {};
					}

					// Добавляем базовые параметры
					Object.entries(this.params).forEach(([key, value]) => {
						if (!data.hasOwnProperty(key)) {
							data[key] = value;
						}
					});

					// Выполняем действие
					await this.performAction(actionName, data);

					// Показываем уведомление об успехе
					if (config.successMessage) {
						this._showNotification(config.successMessage, 'success');
					}

				} catch (error) {
					// Показываем уведомление об ошибке
					const errorMessage = config.errorMessage || `Ошибка при выполнении ${actionName}`;
					this._showNotification(errorMessage, 'error');
					throw error;
				}
			};
		});
	}

	/**
	 * Выполняет AJAX запрос и обновляет компонент
	 * @param {string} action - Название действия
	 * @param {Object} data - Данные для отправки
	 * @param {Object} options - Дополнительные опции
	 */
	async performAction(action, data = {}, options = {}) {
		if (this.isDestroyed) {
			throw new Error('Component is destroyed');
		}

		// Оптимизация: если действие уже выполнено в init(), пропускаем повторный запрос
		if (this.initialActionPerformed && this.params.action === action) {
			console.log(`[VROC] Skipping duplicate action: ${action} (already performed in init)`);

			// Вызываем события для поддержания совместимости
			this._triggerEvent('beforeAction', { action, data });
			this._triggerEvent('afterAction', { action, data, response: 'skipped' });

			return 'skipped'; // Возвращаем маркер, что запрос был пропущен
		}

		try {
			// Валидация данных
			if (this.validation[action]) {
				const validationResult = this._validateData(data, this.validation[action]);
				if (!validationResult.isValid) {
					throw new Error(`Validation failed: ${validationResult.errors.join(', ')}`);
				}
			}
			
			// Кастомная валидация
			if (this.customValidation[action]) {
				let validationData = data;

				// Если data - FormData, конвертируем в объект для кастомной валидации
				if (data instanceof FormData) {
					validationData = {};
					for (let [key, value] of data.entries()) {
						if (!validationData[key]) {
							validationData[key] = value;
						} else {
							// Обработка массивов
							if (!Array.isArray(validationData[key])) {
								validationData[key] = [validationData[key]];
							}
							validationData[key].push(value);
						}
					}
				}

				const customValidationResult = this.customValidation[action](validationData);
				if (!customValidationResult.isValid) {
					throw new Error(`Custom validation failed: ${customValidationResult.errors.join(', ')}`);
				}
			}

			// Вызываем событие beforeAction
			let eventData = data;
			if (data instanceof FormData) {
				// Для события передаем объект с информацией о FormData
				eventData = {
					_formData: true,
					action: action,
					entries: Array.from(data.entries())
				};
			}
			this._triggerEvent('beforeAction', { action, data: eventData });

			let formData;

			// Если data уже является FormData, используем его
			if (data instanceof FormData) {
				formData = data;

				// Добавляем базовые параметры (кроме тех, что уже есть в FormData)
				/*Object.entries(this.params).forEach(([key, value]) => {
					if (!formData.has(key)) {
						formData.append(key, value);
					}
				});*/

				// Убеждаемся, что action есть в FormData
				if (!formData.has('action')) {
					formData.append('action', action);
				}
			} else {
				// Создаем новый FormData для обычных данных
				formData = new FormData();

				// Добавляем базовые параметры (кроме action, если он там есть)
				/*Object.entries(this.params).forEach(([key, value]) => {
					if (key !== 'action') { // Избегаем дублирования action
						formData.append(key, value);
					}
				});*/

				// Добавляем action
				formData.append('action', action);

				// Добавляем данные действия
				Object.entries(data).forEach(([key, value]) => {
					// Если значение - массив, добавляем каждый элемент отдельно
					if (Array.isArray(value)) {
						value.forEach(item => {
							formData.append(`${key}[]`, item);
						});
					} else {
						formData.append(key, value);
					}
				});
			}

			// Логируем содержимое FormData для отладки
			const formDataEntries = Array.from(formData.entries());
			console.log(`[VROC] Sending FormData for action "${action}" with ${formDataEntries.length} fields:`);
			formDataEntries.forEach(([key, value]) => {
				console.log(`  ${key}: ${value}`);
			});

			const response = await fetch(`/ajax.php?mid=${this.moduleId}`, {
				method: 'POST',
				body: formData,
				...options
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const xmlText = await response.text();
			await this.vroc.updateComponent(this.id, xmlText);

			// Вызываем событие afterAction
			let afterEventData = data;
			if (data instanceof FormData) {
				// Для события передаем объект с информацией о FormData
				afterEventData = {
					_formData: true,
					action: action,
					entries: Array.from(data.entries())
				};
			}
			this._triggerEvent('afterAction', { action, data: afterEventData, response: xmlText });

			return xmlText;

		} catch (error) {
			this._handleError(`Action "${action}" failed`, error);
			throw error;
		}
	}

	/**
	 * Обновляет компонент с новыми параметрами
	 * @param {Object} newParams - Новые параметры
	 */
	async updateParams(newParams) {
		if (this.isDestroyed) {
			throw new Error('Component is destroyed');
		}

		try {
			// Вызываем событие beforeUpdateParams
			this._triggerEvent('beforeUpdateParams', { oldParams: this.params, newParams });

			this.params = { ...this.params, ...newParams };
			
			const urlParams = new URLSearchParams({
				mid: this.moduleId,
				action: this.config.name,
				...this.params
			});

			const response = await fetch(`/ajax.php?${urlParams.toString()}`);
			const xmlText = await response.text();
			await this.vroc.updateComponent(this.id, xmlText);

			// Вызываем событие afterUpdateParams
			this._triggerEvent('afterUpdateParams', { params: this.params });

		} catch (error) {
			this._handleError('Update params failed', error);
			throw error;
		}
	}

	/**
	 * Показывает компонент (если это попап)
	 */
	show() {
		if (this.isDestroyed) return;

		if (this.isPopup && this.popupName) {
			// Используем PopupManager для попапов
			popupManager.open(this.popupName);
		} else {
			// Обычное показывание для не-попапов
			const container = document.querySelector(this.config.node);
			if (container) {
				container.classList.add('active');
			}
		}
		
		this._triggerEvent('shown');
	}

	/**
	 * Скрывает компонент (если это попап)
	 */
	hide() {
		if (this.isDestroyed || this._isBeingDestroyed) return;

		if (this.isPopup && this.popupName) {
			// Используем PopupManager для попапов
			popupManager.close(this.popupName);
		} else {
			// Обычное скрытие для не-попапов
			const container = document.querySelector(this.config.node);
			if (container) {
				container.classList.remove('active');
			}
		}
		
		// Не вызываем событие hidden если компонент уничтожается
		if (!this._isBeingDestroyed) {
			this._triggerEvent('hidden');
		}
	}

	/**
	 * Уничтожает компонент
	 */
	destroy() {
		if (this.isDestroyed) return;

		// Устанавливаем флаг, что компонент уничтожается (для предотвращения событий)
		this._isBeingDestroyed = true;
		
		// Устанавливаем флаг, что компонент не готов
		this.isReady = false;
		this.initialActionPerformed = false;

		// Вызываем событие beforeDestroy
		this._triggerEvent('beforeDestroy');

		// Отписываемся от событий PopupManager
		if (this._popupCloseHandler && window.popupManager) {
			window.popupManager.off('popupClosed', this._popupCloseHandler);
			this._popupCloseHandler = null;
		}

		this.vroc.detachComponent(this.id);
		
		// Скрываем компонент (метод hide теперь защищен от рекурсии)
		this.hide();
		
		this.isDestroyed = true;

		// Вызываем событие afterDestroy
		this._triggerEvent('afterDestroy');
	}

	/**
	 * Показывает уведомление
	 * @param {string} message - Сообщение
	 * @param {string} type - Тип уведомления (success, error, warning, info)
	 */
	_showNotification(message, type = 'info') {
		// Если есть кастомная функция для уведомлений
		if (this.notifications.show) {
			this.notifications.show(message, type);
			return;
		}

		// Стандартная реализация
		console.log(`[${type.toUpperCase()}] ${message}`);
		
		// Можно добавить интеграцию с любой системой уведомлений
		// Например: toast.success(message) или alert(message)
	}

	/**
	 * Валидирует данные
	 * @param {Object} data - Данные для валидации
	 * @param {Object} rules - Правила валидации
	 * @returns {Object} - Результат валидации
	 */
	_validateData(data, rules) {
		const errors = [];

		Object.entries(rules).forEach(([field, rule]) => {
			let value;

			// Поддержка как обычных объектов, так и FormData
			if (data instanceof FormData) {
				value = data.get(field);
			} else {
				value = data[field];
			}

			if (rule.required && (value === undefined || value === null || value === '')) {
				errors.push(`${field} is required`);
			}

			if (rule.type && value !== undefined && value !== null) {
				if (rule.type === 'number' && isNaN(Number(value))) {
					errors.push(`${field} must be a number`);
				}
				if (rule.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
					errors.push(`${field} must be a valid email`);
				}
			}

			if (rule.minLength && value && value.length < rule.minLength) {
				errors.push(`${field} must be at least ${rule.minLength} characters`);
			}

			if (rule.maxLength && value && value.length > rule.maxLength) {
				errors.push(`${field} must be no more than ${rule.maxLength} characters`);
			}
		});

		return {
			isValid: errors.length === 0,
			errors
		};
	}

	/**
	 * Обрабатывает ошибки
	 * @param {string} message - Сообщение об ошибке
	 * @param {Error} error - Объект ошибки
	 */
	_handleError(message, error) {
		console.error(`[${this.config.name}] ${message}:`, error);

		// Вызываем событие error
		this._triggerEvent('error', { message, error });

		// Вызываем пользовательский обработчик ошибок
		if (this.onError) {
			try {
				this.onError.call(this, message, error);
			} catch (handlerError) {
				console.error(`Error in onError handler:`, handlerError);
			}
		}
	}

	/**
	 * Вызывает событие компонента
	 * @param {string} eventName - Название события
	 * @param {Object} data - Данные события
	 */
	_triggerEvent(eventName, data = {}) {
		if (this.events[eventName]) {
			// Проверяем, является ли обработчик массивом или функцией
			const handlers = Array.isArray(this.events[eventName]) 
				? this.events[eventName] 
				: [this.events[eventName]];
			
			handlers.forEach(handler => {
				if (typeof handler === 'function') {
					try {
						handler.call(this, data);
					} catch (error) {
						console.error(`Error in event handler for ${eventName}:`, error);
					}
				}
			});
		}
	}

	/**
	 * Подписывается на события компонента
	 * @param {string} eventName - Название события
	 * @param {Function} handler - Обработчик события
	 */
	on(eventName, handler) {
		if (!this.events[eventName]) {
			this.events[eventName] = [];
		} else if (!Array.isArray(this.events[eventName])) {
			// Если обработчик уже существует как функция, преобразуем в массив
			this.events[eventName] = [this.events[eventName]];
		}
		this.events[eventName].push(handler);
	}

	/**
	 * Отписывается от событий компонента
	 * @param {string} eventName - Название события
	 * @param {Function} handler - Обработчик события
	 */
	off(eventName, handler) {
		if (this.events[eventName]) {
			if (Array.isArray(this.events[eventName])) {
				const handlers = this.events[eventName];
				const index = handlers.indexOf(handler);
				if (index > -1) {
					handlers.splice(index, 1);
				}
			} else if (this.events[eventName] === handler) {
				// Если обработчик - это функция, удаляем её
				delete this.events[eventName];
			}
		}
	}
}

/**
 * Менеджер компонентов для динамического создания и управления жизненным циклом
 */
export class VROCComponentManager {
	constructor() {
		this.factory = new VROCComponentFactory();
		this.activeComponents = new Map(); // Активные компоненты по ID
		this.componentConfigs = new Map(); // Базовые конфигурации компонентов
	}

	/**
	 * Регистрирует базовую конфигурацию компонента
	 * @param {string} name - Название компонента
	 * @param {Object} baseConfig - Базовая конфигурация
	 */
	registerComponent(name, baseConfig) {
		this.componentConfigs.set(name, baseConfig);
	}

	/**
	 * Создает и инициализирует компонент с динамическими параметрами
	 * @param {string} name - Название компонента
	 * @param {Object} dynamicParams - Динамические параметры (template, actions, etc.)
	 * @returns {Promise<VROCComponent>} - Промис с компонентом
	 */
	async createComponent(name, dynamicParams = {}) {
		const baseConfig = this.componentConfigs.get(name);
		if (!baseConfig) {
			throw new Error(`Component "${name}" is not registered`);
		}

		// Создаем уникальный ID для этого экземпляра
		const instanceId = `${name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

		// Объединяем базовую конфигурацию с динамическими параметрами
		const config = {
			...baseConfig,
			...dynamicParams,
			name: instanceId, // Уникальное имя для фабрики
			originalName: name // Сохраняем оригинальное имя
		};

		// Создаем компонент через фабрику
		const component = this.factory.createComponent(config);

		// Сохраняем ссылку на активный компонент
		this.activeComponents.set(instanceId, {
			component,
			originalName: name,
			createdAt: Date.now()
		});

		// Ждем инициализации компонента
		await new Promise((resolve) => {
			const checkReady = () => {
				if (component.isReady === true) { // Ждем именно true
					resolve();
				} else {
					setTimeout(checkReady, 10);
				}
			};
			setTimeout(checkReady, 10);
		});

		// Настраиваем автоматическое уничтожение при закрытии попапа
		// Исключение: компоненты ServiceManager управляются вручную
		if (name !== 'serviceManager') {
			this._setupAutoDestroy(instanceId, component);
		}

		return component;
	}

	/**
	 * Уничтожает компонент по ID
	 * @param {string} instanceId - ID экземпляра компонента
	 */
	destroyComponent(instanceId) {
		const componentData = this.activeComponents.get(instanceId);
		if (componentData) {
			this.factory.destroyComponent(instanceId);
			this.activeComponents.delete(instanceId);
		}
	}

	/**
	 * Уничтожает все компоненты с определенным именем
	 * @param {string} name - Название компонента
	 */
	destroyComponentsByName(name) {
		const toDestroy = [];
		this.activeComponents.forEach((data, instanceId) => {
			if (data.originalName === name) {
				toDestroy.push(instanceId);
			}
		});

		toDestroy.forEach(instanceId => this.destroyComponent(instanceId));
	}

	/**
	 * Получает активный компонент по ID
	 * @param {string} instanceId - ID экземпляра компонента
	 * @returns {VROCComponent|null}
	 */
	getComponent(instanceId) {
		const data = this.activeComponents.get(instanceId);
		return data ? data.component : null;
	}

	/**
	 * Получает все активные компоненты определенного типа
	 * @param {string} name - Название компонента
	 * @returns {Array<VROCComponent>}
	 */
	getComponentsByName(name) {
		const components = [];
		this.activeComponents.forEach((data) => {
			if (data.originalName === name) {
				components.push(data.component);
			}
		});
		return components;
	}

	/**
	 * Уничтожает все активные компоненты
	 */
	destroyAll() {
		const instanceIds = Array.from(this.activeComponents.keys());
		instanceIds.forEach(id => this.destroyComponent(id));
	}

	/**
	 * Настраивает автоматическое уничтожение компонента при закрытии попапа
	 * @param {string} instanceId - ID экземпляра компонента
	 * @param {VROCComponent} component - Компонент
	 */
	_setupAutoDestroy(instanceId, component) {
		if (component.isPopup && component.popupName) {
			// Подписываемся на событие скрытия попапа
			component.on('hidden', () => {
				console.log(`Auto-destroying component ${instanceId} after popup close`);
				this.destroyComponent(instanceId);
			});
		}
	}

	/**
	 * Обновляет шаблон существующего компонента
	 * @param {string} instanceId - ID экземпляра компонента
	 * @param {string} newTemplate - Новый шаблон
	 */
	updateComponentTemplate(instanceId, newTemplate) {
		const component = this.getComponent(instanceId);
		if (component) {
			component.config.template = newTemplate;
			// Здесь можно добавить логику перезагрузки компонента с новым шаблоном
			// Пока просто обновляем конфигурацию
		}
	}

	/**
	 * Обновляет действия компонента
	 * @param {string} instanceId - ID экземпляра компонента
	 * @param {Object} newActions - Новые действия
	 */
	updateComponentActions(instanceId, newActions) {
		const component = this.getComponent(instanceId);
		if (component) {
			// Обновляем действия в конфигурации
			component.config.autoActions = { ...component.config.autoActions, ...newActions };

			// Перегенерируем действия
			component._generateAutoActions();
		}
	}

	/**
	 * Обновляет параметры компонента и перезагружает данные
	 * @param {string} instanceId - ID экземпляра компонента
	 * @param {Object} newParams - Новые параметры
	 * @param {boolean} reloadData - Нужно ли перезагрузить данные с сервера
	 */
	async updateComponentParams(instanceId, newParams, reloadData = true) {
		const component = this.getComponent(instanceId);
		if (component) {
			component.params = { ...component.params, ...newParams };

			if (reloadData) {
				// Перезагружаем данные с новыми параметрами
				const urlParams = new URLSearchParams({
					mid: component.moduleId,
					action: component.config.originalName || component.config.name.replace(/-\d+-[^-]+$/g, ''), // Убираем уникальный ID
					...component.params
				});

				try {
					const response = await fetch(`/ajax.php?${urlParams.toString()}`);
					const xmlText = await response.text();
					await component.vroc.updateComponent(component.id, xmlText);
				} catch (error) {
					console.error(`Error reloading component ${instanceId}:`, error);
				}
			}
		}
	}
}

// Экспортируем фабрику как синглтон для обратной совместимости
export const vrocFactory = new VROCComponentFactory();

// Экспортируем менеджер как синглтон
export const vrocManager = new VROCComponentManager();
